// lib/api.ts
import { LoginResponse, SessionResponse, RefreshTokenResponse, ApiResponse, User, VehiclesResponse, Vehicle, DriversResponse, Driver, OwnersResponse, Owner, CheckVehicleResponse, VerificationStartResponse, VerificationSaveResponse, VerificationDocumentsResponse } from '@/types/auth';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.taxi-yaounde.com/api';

interface RequestOptions extends RequestInit {
  headers?: Record<string, string>;
}

export class ApiError extends Error {
  code: string;
  details?: any;

  constructor(message: string, code: string = 'UNKNOWN_ERROR', details?: any) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
  }
}

class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // private async request<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  //   const url = `${this.baseUrl}${endpoint}`;
  //   const headers: Record<string, string> = {
  //     'Content-Type': 'application/json',
  //     ...options.headers,
  //   };

  //   // Add auth token if available
  //   const token = localStorage.getItem('accessToken');
  //   if (token) {
  //     headers['Authorization'] = `Bearer ${token}`;
  //   }

  //   const config: RequestInit = {
  //     ...options,
  //     headers,
  //   };

  //   try {
  //     const response = await fetch(url, config);
  //     const data: ApiResponse<T> = await response.json();

  //     if (!response.ok) {
  //       // Handle API errors with proper structure
  //       const errorData = data as ApiResponse;
  //       throw new ApiError(
  //         errorData.error?.message || 'API request failed',
  //         errorData.error?.code || 'SERVER_ERROR',
  //         errorData.error?.details
  //       );
  //     }

  //     return data.data as T;
  //   } catch (error) {
  //     if (error instanceof ApiError) {
  //       throw error;
  //     } else if (error instanceof Error) {
  //       throw new ApiError(error.message, 'NETWORK_ERROR');
  //     } else {
  //       throw new ApiError('An unknown error occurred', 'UNKNOWN_ERROR');
  //     }
  //   }
  // }

  // lib/api.ts - Update the request method to log the full response
private async request<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const url = `${this.baseUrl}${endpoint}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add auth token if available
  const token = localStorage.getItem('accessToken');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    ...options,
    headers,
  };

  try {
    console.log(`🔍 API Request: ${options.method || 'GET'} ${url} - api.ts:93`);
    console.log('Request headers: - api.ts:94', headers);
    
    const response = await fetch(url, config);
    
    console.log(`🔍 API Response: ${response.status} ${response.statusText} - api.ts:98`);
    
    // Get response text first
    const responseText = await response.text();
    console.log('🔍 Raw response text: - api.ts:102', responseText.substring(0, 500));
    
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (parseError) {
      console.error('❌ Failed to parse JSON: - api.ts:108', responseText);
      throw new ApiError('Invalid JSON response', 'INVALID_JSON');
    }
    
    console.log('🔍 Parsed response data: - api.ts:112', data);
    
    if (!response.ok) {
      const errorData = data as ApiResponse;
      console.error('❌ API Error: - api.ts:116', errorData);
      throw new ApiError(
        errorData.error?.message || 'API request failed',
        errorData.error?.code || 'SERVER_ERROR',
        errorData.error?.details
      );
    }

    // Return the FULL response, not just data.data
    console.log('✅ API request successful - api.ts:125');
    return data as T;
    
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    } else if (error instanceof Error) {
      throw new ApiError(error.message, 'NETWORK_ERROR');
    } else {
      throw new ApiError('An unknown error occurred', 'UNKNOWN_ERROR');
    }
  }
}

  // Auth methods

  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    // The request method now returns the full response
    return response as LoginResponse;
  }

  async logout(): Promise<{ success: boolean; message: string }> {
    return this.request('/auth/logout', {
      method: 'POST',
    });
  }

  async getSession(): Promise<SessionResponse> {
    return this.request<SessionResponse>('/auth/session');
  }

  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    return this.request<RefreshTokenResponse>('/auth/refresh-token', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });
  }

  async checkEmail(email: string): Promise<{ success: boolean; data: { exists: boolean } }> {
    return this.request(`/auth/check-email/${email}`);
  }

  // lib/api.ts - Add these methods to ApiClient class
async getAdmins(): Promise<{ success: boolean; data: { users: User[] } }> {
  return this.request('/auth/admins');
}

async createAdmin(adminData: {
  name: string;
  email: string;
  password: string;
  role: User['role'];
  phone?: string;
}): Promise<{ success: boolean; data: { user: User }; message: string }> {
  return this.request('/auth/register', {
    method: 'POST',
    body: JSON.stringify(adminData),
  });
}

async updateAdmin(id: number | string, adminData: {
  name?: string;
  email?: string;
  phone?: string;
  role?: User['role'];
  status?: User['status'];
}): Promise<{ success: boolean; data: { user: User }; message: string }> {
  return this.request(`/auth/admins/${id}`, {
    method: 'PUT',
    body: JSON.stringify(adminData),
  });
}

async deleteAdmin(id: number | string): Promise<{ success: boolean; message: string }> {
  return this.request(`/auth/admins/${id}`, {
    method: 'DELETE',
  });
}
async changePassword(data: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<{ success: boolean; message: string }> {
  return this.request('/auth/change-password', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

async getVehicles(params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  zone?: string;
}): Promise<VehiclesResponse> {
  const queryParams = new URLSearchParams();
  
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.zone) queryParams.append('zone', params.zone);
  
  const queryString = queryParams.toString();
  const endpoint = `/vehicles${queryString ? `?${queryString}` : ''}`;
  
  return this.request(endpoint);
}

async getVehicle(id: number | string): Promise<{ success: boolean; data: { vehicle: Vehicle; insuranceHistory: any[] } }> {
  return this.request(`/vehicles/${id}`);
}

async getVehicleZones(): Promise<{ success: boolean; data: { zones: string[] } }> {
  return this.request('/vehicles/zones');
}

// Add to your ApiClient class in lib/api.ts
async getDrivers(params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  gender?: string;
}): Promise<DriversResponse> {
  const queryParams = new URLSearchParams();
  
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.gender) queryParams.append('gender', params.gender);
  
  const queryString = queryParams.toString();
  const endpoint = `/drivers${queryString ? `?${queryString}` : ''}`;
  
  return this.request(endpoint);
}

async getDriver(id: number | string): Promise<{ success: boolean; data: { driver: Driver } }> {
  return this.request(`/drivers/${id}`);
}

async getDriverVehicles(id: number | string): Promise<{ success: boolean; data: { vehicles: Vehicle[] } }> {
  return this.request(`/drivers/${id}/vehicles`);
}

async getLicenseCategories(): Promise<{ success: boolean; data: { categories: string[] } }> {
  return this.request('/drivers/license-categories');
}


async getOwners(params?: {
  page?: number;
  limit?: number;
  search?: string;
  taxisFilter?: string;
  type?: string;
}): Promise<OwnersResponse> {
  const queryParams = new URLSearchParams();
  
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.taxisFilter) queryParams.append('taxisFilter', params.taxisFilter);
  if (params?.type) queryParams.append('type', params.type);
  
  const queryString = queryParams.toString();
  const endpoint = `/owners${queryString ? `?${queryString}` : ''}`;
  
  return this.request(endpoint);
}

async getOwner(id: number | string): Promise<{ success: boolean; data: { owner: Owner } }> {
  return this.request(`/owners/${id}`);
}

async getOwnerVehicles(id: number | string): Promise<{ success: boolean; data: { vehicles: Vehicle[] } }> {
  return this.request(`/owners/${id}/vehicles`);
}

// --- Verification flow ---

async checkVehicle(numeimma: string): Promise<CheckVehicleResponse> {
  return this.request("/verification/check-vehicle", {
    method: "POST",
    body: JSON.stringify({ numeimma }),
  });
}

async startVerification(payload: {
  numeimma: string;
  owner: any;
  driver?: any;
}): Promise<VerificationStartResponse> {
  return this.request("/verification/start", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

async saveVerificationMissingData(payload: {
  verificationId: string;
  ownerUpdates?: any;
  vehicleUpdates?: any;
  driverUpdates?: any;
}): Promise<VerificationSaveResponse> {
  return this.request("/verification/save", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

async registerVerificationDocuments(payload: {
  verificationId: string;
  documents: Array<{
    docType: string;      // 'CARTE_GRISE', ...
    bucket: string;       // supabase bucket
    path: string;         // storage path
    originalName?: string;
    mimeType?: string;
    sizeBytes?: number;
  }>;
}): Promise<VerificationDocumentsResponse> {
  return this.request("/verification/documents", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

}



export const api = new ApiClient();